import { useEffect } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import type { ISignalProviderDetailResponse } from '@brushfire/types'
import {
  CHILDREN_OPTIONS,
  EDUCATION_OPTIONS,
  EMPLOYMENT_OPTIONS,
  GENDER_OPTIONS,
  HOUSING_OPTIONS,
  INDUSTRY_OPTIONS,
  MARITAL_OPTIONS,
  US_STATES,
} from '@/features/instruments/publish/target-audience/lib/demographicFilterCatalog'
import { formatUsPhoneCell } from '@/shared/lib/formatUsNanpDisplay'
import { useToast } from '@/shared/toast'
import { useSignalProviderDetail } from '../api/signal-provider-list-queries'
import {
  SP_DETAIL_AVATAR_BG,
  SP_DETAIL_AVATAR_SIZE_PX,
  SP_DETAIL_DEMO_PANEL_BG,
  SP_DETAIL_DEMO_PANEL_BORDER,
  SP_DETAIL_DEMO_PANEL_RADIUS_PX,
  SP_DETAIL_MODAL_BORDER,
  SP_DETAIL_MODAL_RADIUS_PX,
  SP_DETAIL_MODAL_WIDTH_PX,
  SP_DETAIL_PADDING_X_PX,
  SP_DETAIL_STAT_TILE_GAP_PX,
  SP_DETAIL_STAT_TILE_HEIGHT_PX,
  SP_DETAIL_STAT_TILE_RADIUS_PX,
  SP_DETAIL_STAT_TILES,
} from '../lib/signal-provider-detail-layout'
import { SignalProviderDetailModalSkeleton } from './SignalProviderDetailModalSkeleton'
import { SignalProviderVerifiedPill } from './SignalProviderVerifiedPill'

function labelFor(options: { value: string; label: string }[], value: string | null | undefined): string {
  if (!value) return '—'
  return options.find((o) => o.value === value)?.label ?? value
}

function formatUsdCompact(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDob(dateOfBirth: string | null | undefined): string {
  if (!dateOfBirth) return '—'
  const datePart = dateOfBirth.includes('T') ? dateOfBirth.slice(0, 10) : dateOfBirth
  const parsed = dayjs(datePart)
  if (!parsed.isValid()) return '—'
  return parsed.format('MM/DD/YYYY')
}

function formatLocation(
  location: { state?: string; city?: string } | null | undefined,
  stateLabelFor: (value: string | null | undefined) => string,
): string {
  const stateLabel = location?.state ? stateLabelFor(location.state) : ''
  const city = location?.city?.trim() ?? ''
  if (stateLabel && city) return `${stateLabel}, ${city}`
  if (stateLabel) return stateLabel
  if (city) return city
  return '—'
}

function householdDisplay(value: string | null | undefined): string {
  if (!value) return '—'
  if (value === '7plus') return '7+'
  return value
}

function formatDeviceTypes(deviceTypes: string[]): string {
  if (!deviceTypes.length) return '—'
  const labels: Record<string, string> = {
    mobile: 'Mobile',
    web: 'Web',
  }
  return deviceTypes.map((type) => labels[type] ?? type).join(', ')
}

interface DemographicRowProps {
  label: string
  value: string
}

function DemographicRow({ label, value }: DemographicRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        minHeight: 24,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 400,
          lineHeight: '24px',
          color: '#0f172a',
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 700,
          lineHeight: '24px',
          color: '#0f172a',
          textAlign: 'right',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

interface Props {
  platformUserId: string | null
  open: boolean
  onClose: () => void
}

function DetailContent({ data, onClose }: { data: ISignalProviderDetailResponse; onClose: () => void }) {
  const { card, stats } = data
  const demo = card.demographics
  const displayName = `${card.firstName} ${card.lastName}`.trim()
  const initials = `${card.firstName[0] ?? ''}${card.lastName[0] ?? ''}`.toUpperCase()
  const identityLine = [card.signalProviderId, card.email].filter(Boolean).join(' · ')

  const statValues = [
    String(stats.instrumentsAttended),
    formatUsdCompact(stats.totalEarningsUsd),
    formatUsdCompact(stats.totalRedeemedUsd),
    formatUsdCompact(stats.availableBalanceUsd),
  ]

  const leftDemographics: DemographicRowProps[] = [
    { label: 'DOB', value: formatDob(demo.dateOfBirth) },
    {
      label: 'Location',
      value: formatLocation(demo.location, (state) => labelFor(US_STATES, state)),
    },
    { label: 'Employment Status', value: labelFor(EMPLOYMENT_OPTIONS, demo.employment) },
    { label: 'Household Size', value: householdDisplay(demo.householdSize) },
    { label: 'Industry / Field', value: labelFor(INDUSTRY_OPTIONS, demo.industry) },
    { label: 'Device Type', value: formatDeviceTypes(demo.deviceTypes ?? []) },
  ]

  const rightDemographics: DemographicRowProps[] = [
    { label: 'Gender', value: labelFor(GENDER_OPTIONS, demo.gender) },
    { label: 'Education Level', value: labelFor(EDUCATION_OPTIONS, demo.education) },
    { label: 'Marital Status', value: labelFor(MARITAL_OPTIONS, demo.maritalStatus) },
    { label: 'Number of Children', value: labelFor(CHILDREN_OPTIONS, demo.numberOfChildren) },
    { label: 'Housing Status', value: labelFor(HOUSING_OPTIONS, demo.housingStatus) },
    { label: 'Phone', value: formatUsPhoneCell(card.phone) },
  ]

  return (
    <Box
      sx={{ px: `${SP_DETAIL_PADDING_X_PX}px`, pb: '20px', pt: '16px' }}
      data-testid="signal-provider-detail-modal-content"
    >
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          pb: '14px',
          mb: '18px',
          borderBottom: `1px solid ${SP_DETAIL_MODAL_BORDER}`,
        }}
      >
        <Box
          sx={{
            width: SP_DETAIL_AVATAR_SIZE_PX,
            height: SP_DETAIL_AVATAR_SIZE_PX,
            borderRadius: '50%',
            bgcolor: SP_DETAIL_AVATAR_BG,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '24px',
            color: '#ffffff',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {card.avatarUrl ? (
            <Box
              component="img"
              src={card.avatarUrl}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            initials
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '24px',
                color: '#0f172a',
              }}
            >
              {displayName}
            </Typography>
            <SignalProviderVerifiedPill status={card.verifiedStatus} size="compact" />
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '22px',
              color: '#0f172a',
              mt: '2px',
              wordBreak: 'break-word',
            }}
          >
            {identityLine}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: `${SP_DETAIL_STAT_TILE_GAP_PX}px`,
          mb: '16px',
        }}
      >
        {SP_DETAIL_STAT_TILES.map((tile, index) => (
          <Box
            key={tile.key}
            data-testid={`signal-provider-stat-${tile.key}`}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              height: SP_DETAIL_STAT_TILE_HEIGHT_PX,
              borderRadius: `${SP_DETAIL_STAT_TILE_RADIUS_PX}px`,
              border: `1px solid ${tile.border}`,
              bgcolor: tile.bg,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              pl: index === 0 ? '12px' : '14px',
              pr: '14px',
              py: '14px',
            }}
          >
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                lineHeight: '28px',
                color: '#0b1c30',
              }}
            >
              {statValues[index]}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 500,
                lineHeight: '16px',
                letterSpacing: '0.12px',
                color: '#3f4850',
                mt: '2px',
              }}
            >
              {tile.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          lineHeight: '28px',
          letterSpacing: '-0.4395px',
          color: '#23262f',
          mb: '10px',
        }}
      >
        Demographic Details
      </Typography>

      <Box
        sx={{
          bgcolor: SP_DETAIL_DEMO_PANEL_BG,
          border: `1px solid ${SP_DETAIL_DEMO_PANEL_BORDER}`,
          borderRadius: `${SP_DETAIL_DEMO_PANEL_RADIUS_PX}px`,
          p: '14px 16px',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '10px 36px',
        }}
        data-testid="signal-provider-detail-demographics"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {leftDemographics.map((row) => (
            <DemographicRow key={row.label} label={row.label} value={row.value} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {rightDemographics.map((row) => (
            <DemographicRow key={row.label} label={row.label} value={row.value} />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          data-testid="signal-provider-detail-close-button"
          sx={{
            height: 40,
            px: '16px',
            py: '12px',
            textTransform: 'none',
            borderRadius: '12px',
            borderWidth: '1.5px',
            borderColor: '#e5e7eb',
            color: '#6b6b6b',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '16px',
            minWidth: 0,
            boxShadow: 'none',
            '&:hover': {
              borderWidth: '1.5px',
              borderColor: '#d1d5db',
              bgcolor: '#fafafa',
              boxShadow: 'none',
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Box>
  )
}

export function SignalProviderDetailModal({ platformUserId, open, onClose }: Props) {
  const { showToast } = useToast()
  const { data, isLoading, isError } = useSignalProviderDetail(
    platformUserId,
    open && !!platformUserId,
  )

  useEffect(() => {
    if (!open || !isError) return
    showToast('Unable to load signal provider details.', 'error')
    onClose()
  }, [open, isError, showToast, onClose])

  const showSkeleton = isLoading || (!data && !isError)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      data-testid="signal-provider-detail-modal"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: SP_DETAIL_MODAL_WIDTH_PX,
          borderRadius: `${SP_DETAIL_MODAL_RADIUS_PX}px`,
          border: `1px solid ${SP_DETAIL_MODAL_BORDER}`,
          bgcolor: '#ffffff',
          m: 2,
          overflow: 'hidden',
        },
      }}
    >
      {open ? (
        showSkeleton ? (
          <SignalProviderDetailModalSkeleton />
        ) : data ? (
          <DetailContent data={data} onClose={onClose} />
        ) : null
      ) : null}
    </Dialog>
  )
}
